/* Frame opening detection — shared between product page and collection cards.
 *
 * Frame PNGs in the catalog have transparent center "openings". This script
 * detects the bounding box of those transparent pixels via canvas, returning
 * the opening as ratios of the PNG dimensions:
 *   { openW, openH, cx, cy }  — all 0..1
 *
 * Results are cached in memory + localStorage so each frame is detected
 * exactly once across the whole user session.
 *
 * Used by:
 *   - main-product-drehmer.liquid (frame picker on product page)
 *   - main-collection-art-browse.liquid (random framed cards)
 */
(function () {
  if (window.ArtAndSeeFrame) return; // single-load guard

  var MEM_CACHE = {};
  var LS_KEY = 'aas_frame_openings_v1';

  function loadLSCache() {
    try {
      var raw = localStorage.getItem(LS_KEY);
      if (raw) {
        var obj = JSON.parse(raw);
        for (var k in obj) MEM_CACHE[k] = obj[k];
      }
    } catch (e) {}
  }
  function saveLSCache() {
    try { localStorage.setItem(LS_KEY, JSON.stringify(MEM_CACHE)); } catch (e) {}
  }
  loadLSCache();

  /**
   * detectFrameOpening(frameSrc, callback)
   * Loads the frame PNG, scans for transparent pixels (alpha < 10), and
   * returns the bounding rect as ratios. Cached forever per src.
   */
  function detectFrameOpening(frameSrc, callback) {
    if (!frameSrc) { callback(null); return; }
    if (Object.prototype.hasOwnProperty.call(MEM_CACHE, frameSrc)) {
      callback(MEM_CACHE[frameSrc]);
      return;
    }
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      var c = document.createElement('canvas');
      c.width = img.naturalWidth;
      c.height = img.naturalHeight;
      var ctx = c.getContext('2d');
      ctx.drawImage(img, 0, 0);
      var id;
      try {
        id = ctx.getImageData(0, 0, c.width, c.height);
      } catch (e) {
        // Tainted canvas (CORS) — give up
        MEM_CACHE[frameSrc] = null;
        callback(null);
        return;
      }
      var d = id.data;
      var top = c.height, bot = 0, left = c.width, right = 0;
      // Sample every 2px for speed
      for (var y = 0; y < c.height; y += 2) {
        for (var x = 0; x < c.width; x += 2) {
          if (d[(y * c.width + x) * 4 + 3] < 10) {
            if (y < top) top = y;
            if (y > bot) bot = y;
            if (x < left) left = x;
            if (x > right) right = x;
          }
        }
      }
      var result = null;
      if (top < bot && left < right) {
        result = {
          openW: (right - left) / c.width,
          openH: (bot - top) / c.height,
          cx: (left + right) / 2 / c.width,
          cy: (top + bot) / 2 / c.height
        };
      }
      MEM_CACHE[frameSrc] = result;
      saveLSCache();
      callback(result);
    };
    img.onerror = function () {
      MEM_CACHE[frameSrc] = null;
      callback(null);
    };
    img.src = frameSrc;
  }

  /**
   * fitPaintingInOpening(paintingEl, opening, opts)
   * Applies the transform that scales + translates the painting so it lands
   * exactly inside the frame opening. Mirrors fitPaintingInsideFrame() from
   * main-product-drehmer.liquid.
   */
  function fitPaintingInOpening(paintingEl, opening, opts) {
    if (!paintingEl || !opening) return;
    var bleed = (opts && typeof opts.bleed === 'number') ? opts.bleed : 0;
    var scale = Math.max(opening.openW, opening.openH) + bleed;
    var offsetX = (opening.cx - 0.5) * 100;
    var offsetY = (opening.cy - 0.5) * 100;
    paintingEl.style.transformOrigin = 'center center';
    paintingEl.style.transform =
      'scale(' + scale + ') translate(' + offsetX + '%, ' + offsetY + '%)';
  }

  /**
   * applyFrameToRoomComposite(wrapEl)
   * Given a `.drehmer__room-painting-wrap` element that contains a painting
   * (`.drehmer__room-composite-painting`) and a frame overlay
   * (`.drehmer__room-frame-overlay`), detects the frame's transparent opening
   * and applies a transform to the painting so it lands inside the opening.
   * This mirrors the product page's room composite frame behavior.
   * Idempotent — safe to call repeatedly.
   */
  function lockWrapHeight(wrapEl) {
    // Wrap CSS has `height: auto !important`, but inline `aspect-ratio` should
    // resolve the height from the inline width%. In some browsers / nested
    // contexts that fails and the wrap collapses to intrinsic content height,
    // making absolute children with `height: 100%` mis-size. Solution: set the
    // wrap height directly in pixels from its measured width and the inline
    // aspect ratio. Painting + frame both at width/height 100% then resolve
    // correctly against this concrete height.
    var ar = wrapEl.style.aspectRatio || '';
    var parts = ar.split('/');
    if (parts.length !== 2) return;
    var arNum = parseFloat(parts[0]) / parseFloat(parts[1]);
    if (!arNum || !isFinite(arNum)) return;
    var w = wrapEl.offsetWidth;
    if (!w) return;
    var h = Math.round(w / arNum);
    wrapEl.style.setProperty('height', h + 'px', 'important');
  }

  function applyFrameToRoomComposite(wrapEl) {
    if (!wrapEl) return;
    var painting = wrapEl.querySelector('.drehmer__room-composite-painting');
    var frame = wrapEl.querySelector('.drehmer__room-frame-overlay');
    if (!painting || !frame || !frame.src) return;
    lockWrapHeight(wrapEl);
    if (wrapEl.__aasFrameApplied) return;
    wrapEl.__aasFrameApplied = true;
    detectFrameOpening(frame.src, function (opening) {
      if (!opening) return;
      // Bleed: tiny offset so the painting fully covers the opening edge
      var w = wrapEl.offsetWidth || 200;
      fitPaintingInOpening(painting, opening, { bleed: 2 / w });
      wrapEl.classList.add('has-frame-fit');
    });
    // Re-lock height when the parent (card / masonry) changes size. Observe
    // the parent .drehmer__room-composite, NOT the wrap itself, to avoid the
    // resize → setHeight → resize feedback loop.
    var composite = wrapEl.closest('.drehmer__room-composite');
    if (composite && typeof ResizeObserver !== 'undefined' && !composite.__aasResizeBound) {
      composite.__aasResizeBound = true;
      var ro = new ResizeObserver(function () {
        lockWrapHeight(wrapEl);
      });
      ro.observe(composite);
    }
  }

  /**
   * applyFramesToAll(rootEl)
   * Walks all `.drehmer__room-painting-wrap` elements (which carry a frame
   * overlay) under rootEl, and fits each painting inside its frame opening
   * lazily via IntersectionObserver — but observes the parent
   * `.collection-product-card`, not the wrap, since wraps are inside hidden
   * (opacity:0) hover overlays and would never become "intersecting" otherwise.
   */
  function applyFramesToAll(rootEl) {
    var root = rootEl || document;
    var cards = root.querySelectorAll('.collection-product-card');
    if (!cards.length) return;
    function processCard(cardEl) {
      var wraps = cardEl.querySelectorAll('.drehmer__room-painting-wrap');
      for (var i = 0; i < wraps.length; i++) applyFrameToRoomComposite(wraps[i]);
    }
    if (typeof IntersectionObserver === 'undefined') {
      for (var i = 0; i < cards.length; i++) processCard(cards[i]);
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          processCard(e.target);
          io.unobserve(e.target);
        }
      });
    }, { rootMargin: '300px' });
    for (var j = 0; j < cards.length; j++) io.observe(cards[j]);
  }

  window.ArtAndSeeFrame = {
    detectFrameOpening: detectFrameOpening,
    fitPaintingInOpening: fitPaintingInOpening,
    applyFrameToRoomComposite: applyFrameToRoomComposite,
    applyFramesToAll: applyFramesToAll
  };
})();
